import { Box, Button, Flex, Grid, Text } from "@chakra-ui/react";
import { HamburgerIcon } from "@chakra-ui/icons";
import { useEffect, useState } from "react";
import { DropdownField } from "components/dropdown-field";
import { InputField } from "components/input-field";
import { Popover } from "components/popover";
import {
  dropdownEspecies,
  dropdownSimNao,
  dropdownTipoPagamento
} from "components/atendimentos-components/atendimento-form/fields-data";
import { dropdownBancos } from "components/usuarios-bancos-components/consts";
import { especieCodeListConsignaveis } from "components/mailing-components/mailing-modal-filter/consts";
import { csvFields } from "components/campanha-sms-components/modal-campanha-sms/csv-cols";
import { StateProps } from "components/pipeline-components/classes/state-machine/state";
import { StateFilterValue } from "components/pipeline-components/classes/state-machine/filters";
import { MinMaxInput } from "components/pipeline-components/side-panel/components/MinMaxInput";
import api from "api/api";

const csvCols = csvFields.map((key) => ({ name: key, value: key }));

const getMappedFieldsFromMessage = (mensagem: string) => {
  return csvFields
    .filter((field) => mensagem.indexOf(`$${field}`) !== -1)
    .map((field) => `$${field}`);
};

const getMensagemLength = (mensagem: string = "") => {
  const textLength = mensagem.length;
  const uppercaseLength = textLength - mensagem.replace(/[A-Z]/g, "").length;
  return { mensagemLength: textLength + uppercaseLength, uppercaseLength };
};

const sanitizeMensagem = (mensagem: string) => {
  const { uppercaseLength } = getMensagemLength(mensagem);
  const limit = Math.max(0, 160 - uppercaseLength);
  return mensagem.slice(0, limit);
};

const tipoColunaMargemOptions = [
  { name: "Margem Livre", value: "MARGEM_LIVRE" },
  { name: "Margem Aumento", value: "MARGEM_AUMENTO" }
];

const whatsTypeOptions = [{ name: "Texto", value: "TEXT" }];

const especieOptions = especieCodeListConsignaveis.map((cod) => ({
  name:
    dropdownEspecies.find(({ value }) => value === cod)?.name ?? String(cod),
  value: cod
}));

interface WhatsAppSessionOption {
  id: number;
  nome: string;
}

export function hasPreConfigFields(
  operation: StateProps["operation"]
): boolean {
  return ["SAQUE_COMPLEMENTAR", "DIGITACAO", "FILTRO", "SMS", "WHATS"].includes(
    operation
  );
}

export function isPreConfigured(preConfig: Partial<StateProps>): boolean {
  return Object.values(preConfig).some((v) => {
    if (v === undefined || v === "" || v === null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object") return Object.keys(v).length > 0;
    return true;
  });
}

export function BlockPreConfigForm({
  operation,
  preConfig,
  onChange
}: {
  operation: StateProps["operation"];
  preConfig: Partial<StateProps>;
  onChange: (next: Partial<StateProps>) => void;
}) {
  const set = (field: keyof StateProps, value: string) =>
    onChange({ ...preConfig, [field]: value });

  const setNumber = (field: keyof StateProps, value: string) =>
    onChange({
      ...preConfig,
      [field]: value.trim() === "" ? undefined : Number(value)
    });

  const [whatsSessions, setWhatsSessions] = useState<WhatsAppSessionOption[]>(
    []
  );
  const [isLoadingWhatsSessions, setIsLoadingWhatsSessions] = useState(false);

  useEffect(() => {
    if (operation !== "WHATS") return;

    let active = true;

    const loadWhatsSessions = async () => {
      setIsLoadingWhatsSessions(true);
      try {
        const { data } = await api.get<WhatsAppSessionOption[]>(
          "/whatsapp-sessions?only_disparo=true"
        );
        if (active) setWhatsSessions(data ?? []);
      } catch {
        if (active) setWhatsSessions([]);
      } finally {
        if (active) setIsLoadingWhatsSessions(false);
      }
    };

    loadWhatsSessions();

    return () => {
      active = false;
    };
  }, [operation]);

  if (operation === "SAQUE_COMPLEMENTAR") {
    return (
      <Box
        mt="10px"
        p="12px"
        bg="#F7F8FA"
        borderRadius="6px"
        border="1px solid #E2E8F0"
      >
        <Text
          fontSize="11px"
          fontWeight="700"
          letterSpacing="0.08em"
          color="#69778C"
          mb="8px"
        >
          PRE-CONFIGURACAO - SAQUE COMPLEMENTAR
        </Text>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="8px">
          <Box>
            <DropdownField
              title="Banco"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              value={(preConfig.banco as string) ?? ""}
              options={dropdownBancos}
              onChange={(value) => set("banco", value as string)}
              dropdownProps={{ w: "100%", variant: "filterInput" }}
            />
          </Box>
          <Box>
            <InputField
              title="ID Convenio"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              variant="filterInput"
              value={(preConfig.idConvenio as string) ?? ""}
              onChange={(e) => set("idConvenio", e.target.value)}
            />
          </Box>
          <Box>
            <InputField
              title="Tipo Cartao"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              variant="filterInput"
              value={(preConfig.tipoCartao as string) ?? ""}
              onChange={(e) => set("tipoCartao", e.target.value)}
            />
          </Box>
          <Box>
            <InputField
              title="Codigo tabela"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              variant="filterInput"
              value={(preConfig.codigoTabela as string) ?? ""}
              onChange={(e) => set("codigoTabela", e.target.value)}
            />
          </Box>
        </Grid>
      </Box>
    );
  }

  if (operation === "DIGITACAO") {
    return (
      <Box
        mt="10px"
        p="12px"
        bg="#F7F8FA"
        borderRadius="6px"
        border="1px solid #E2E8F0"
      >
        <Text
          fontSize="11px"
          fontWeight="700"
          letterSpacing="0.08em"
          color="#69778C"
          mb="8px"
        >
          PRE-CONFIGURACAO - DIGITACAO
        </Text>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="8px">
          <Box>
            <DropdownField
              title="Banco"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              value={(preConfig.banco as string) ?? ""}
              options={dropdownBancos}
              onChange={(value) => set("banco", value as string)}
              dropdownProps={{ w: "100%", variant: "filterInput" }}
            />
          </Box>
          <Box>
            <InputField
              title="Usuario Digitacao"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              variant="filterInput"
              value={(preConfig.usuarioDigitacao as string) ?? ""}
              onChange={(e) => set("usuarioDigitacao", e.target.value)}
            />
          </Box>
          <Box>
            <InputField
              title="Senha usuario"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              variant="filterInput"
              value={(preConfig.usuarioDigitacaoSenha as string) ?? ""}
              onChange={(e) => set("usuarioDigitacaoSenha", e.target.value)}
            />
          </Box>
          <Box>
            <InputField
              title="CPF certificado"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              variant="filterInput"
              value={(preConfig.cpfUsuarioCertificado as string) ?? ""}
              onChange={(e) => set("cpfUsuarioCertificado", e.target.value)}
            />
          </Box>
          <Box>
            <InputField
              title="Convenio"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              variant="filterInput"
              value={(preConfig.convenio as string) ?? ""}
              onChange={(e) => set("convenio", e.target.value)}
            />
          </Box>
          <Box>
            <InputField
              title="Tabela"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              variant="filterInput"
              value={(preConfig.tabela as string) ?? ""}
              onChange={(e) => set("tabela", e.target.value)}
            />
          </Box>
          <Box>
            <DropdownField
              title="Tipo coluna margem"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              value={(preConfig.tipoColunaMargem as string) ?? ""}
              options={tipoColunaMargemOptions}
              onChange={(value) => set("tipoColunaMargem", value as string)}
              dropdownProps={{ w: "100%", variant: "filterInput" }}
            />
          </Box>
          <Box>
            <InputField
              title="Operacao"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              variant="filterInput"
              value={(preConfig.operacao as string) ?? ""}
              onChange={(e) => set("operacao", e.target.value)}
            />
          </Box>
        </Grid>
      </Box>
    );
  }

  if (operation === "SMS") {
    return (
      <Box
        mt="10px"
        p="12px"
        bg="#F7F8FA"
        borderRadius="6px"
        border="1px solid #E2E8F0"
      >
        <Text
          fontSize="11px"
          fontWeight="700"
          letterSpacing="0.08em"
          color="#69778C"
          mb="8px"
        >
          PRE-CONFIGURACAO - SMS
        </Text>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="8px">
          <Box>
            <InputField
              title="Nome da campanha"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              variant="filterInput"
              value={(preConfig.nome as string) ?? ""}
              onChange={(e) => set("nome", e.target.value)}
            />
          </Box>
          <Box>
            <InputField
              title="Qtd. maxima telefones/lead"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              variant="filterInput"
              value={preConfig.quantidadeMaximaTelLead?.toString() ?? ""}
              inputProps={{ type: "number" }}
              onChange={(e) =>
                setNumber("quantidadeMaximaTelLead", e.target.value)
              }
            />
          </Box>
          <Box gridColumn="1 / -1">
            <Flex mb="8px" justify="space-between" alignItems="center">
              <Text fontSize="12px" color="#4A5568" mb="4px">
                Mensagem
              </Text>
              <Popover
                title="Adicione campos do CSV"
                button={
                  <Button
                    aria-label=""
                    size="xs"
                    variant="outline"
                    leftIcon={<HamburgerIcon />}
                  >
                    Colunas CSV
                  </Button>
                }
                popupStyles={{ width: "auto" }}
                position="left-bottom"
              >
                <Grid
                  w="100%"
                  gap="8px"
                  templateColumns="1fr"
                  overflow="auto"
                  maxH="200px"
                >
                  {csvCols.map(({ name, value }) => (
                    <Button
                      key={value}
                      onClick={() => {
                        const current = (preConfig.mensagem as string) ?? "";
                        const nextMessage = current
                          ? `${current}$${value}`
                          : `$${value}`;
                        const sanitized = sanitizeMensagem(nextMessage);
                        onChange({
                          ...preConfig,
                          mensagem: sanitized,
                          mappedFields: getMappedFieldsFromMessage(sanitized)
                        });
                      }}
                      size="sm"
                      variant="outline"
                    >
                      {name}
                    </Button>
                  ))}
                </Grid>
              </Popover>
            </Flex>
            <InputField
              variant="filterInput"
              value={(preConfig.mensagem as string) ?? ""}
              onChange={(e) => {
                const sanitized = sanitizeMensagem(e.target.value);
                onChange({
                  ...preConfig,
                  mensagem: sanitized,
                  mappedFields: getMappedFieldsFromMessage(sanitized)
                });
              }}
              inputProps={{ as: "textarea", height: "90px", p: "10px 12px" }}
              inputGroupProps={{ height: "90px" }}
            />
            <Text
              mt="8px"
              textAlign="end"
              fontSize="12px"
              color={
                getMensagemLength((preConfig.mensagem as string) ?? "")
                  .mensagemLength > 160
                  ? "var(--chakra-colors-secondary-400)"
                  : "#69778C"
              }
            >
              {
                getMensagemLength((preConfig.mensagem as string) ?? "")
                  .mensagemLength
              }
              /160
            </Text>
          </Box>
        </Grid>
      </Box>
    );
  }

  if (operation === "WHATS") {
    const selectedSessions = (preConfig.wppSessionIds as number[]) ?? [];

    const toggleSession = (sessionId: number) => {
      const next = selectedSessions.includes(sessionId)
        ? selectedSessions.filter((id) => id !== sessionId)
        : [...selectedSessions, sessionId];
      onChange({ ...preConfig, wppSessionIds: next });
    };

    return (
      <Box
        mt="10px"
        p="12px"
        bg="#F7F8FA"
        borderRadius="6px"
        border="1px solid #E2E8F0"
      >
        <Text
          fontSize="11px"
          fontWeight="700"
          letterSpacing="0.08em"
          color="#69778C"
          mb="8px"
        >
          PRE-CONFIGURACAO - WHATS
        </Text>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="8px">
          <Box>
            <InputField
              title="Nome da campanha"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              variant="filterInput"
              value={(preConfig.name as string) ?? ""}
              onChange={(e) => set("name", e.target.value)}
            />
          </Box>
          <Box>
            <InputField
              title="Qtd. envio/dia"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              variant="filterInput"
              value={preConfig.qtdEstimadaEnvioDia?.toString() ?? ""}
              inputProps={{ type: "number" }}
              onChange={(e) => setNumber("qtdEstimadaEnvioDia", e.target.value)}
            />
          </Box>
          <Box>
            <DropdownField
              title="Tipo"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              value={(preConfig.type as string) ?? "TEXT"}
              options={whatsTypeOptions}
              onChange={(value) => set("type", value as string)}
              dropdownProps={{ w: "100%", variant: "filterInput" }}
            />
          </Box>
          <Box>
            <DropdownField
              title="Mensagem Saudacao"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              value={
                preConfig.enabledMensagemSaudacao === undefined
                  ? false
                  : preConfig.enabledMensagemSaudacao
              }
              options={dropdownSimNao}
              onChange={(value) =>
                onChange({
                  ...preConfig,
                  enabledMensagemSaudacao: Boolean(value)
                })
              }
              dropdownProps={{ w: "100%", variant: "filterInput" }}
            />
          </Box>
          <Box gridColumn="1 / -1">
            <InputField
              title="Mensagem"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              variant="filterInput"
              value={(preConfig.mensagemNormal as string) ?? ""}
              onChange={(e) => set("mensagemNormal", e.target.value)}
              inputProps={{ as: "textarea", height: "90px", p: "10px 12px" }}
              inputGroupProps={{ height: "90px" }}
            />
          </Box>
          <Box gridColumn="1 / -1">
            <InputField
              title="Mensagem saudacao"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              variant="filterInput"
              value={(preConfig.mensagemSaudacao as string) ?? ""}
              onChange={(e) => set("mensagemSaudacao", e.target.value)}
              inputProps={{ as: "textarea", height: "80px", p: "10px 12px" }}
              inputGroupProps={{ height: "80px" }}
            />
          </Box>
          <Box gridColumn="1 / -1">
            <DropdownField
              title="Sessões WhatsApp Participantes"
              titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
              value={selectedSessions}
              options={whatsSessions}
              onChange={(value) => toggleSession(value as number)}
              dropdownProps={{
                w: "100%",
                variant: "filterInput",
                multiSelect: true,
                optionValue: "id",
                optionLabel: "nome"
              }}
              isLoading={isLoadingWhatsSessions}
            />
          </Box>
        </Grid>
      </Box>
    );
  }

  if (operation === "FILTRO") {
    return <FiltroPreConfigForm preConfig={preConfig} onChange={onChange} />;
  }

  return null;
}

function FiltroPreConfigForm({
  preConfig,
  onChange
}: {
  preConfig: Partial<StateProps>;
  onChange: (next: Partial<StateProps>) => void;
}) {
  const configJson =
    (preConfig.configJson as Record<string, StateFilterValue> | undefined) ??
    {};

  const upsertConfigValue = (
    key: string,
    value: StateFilterValue | undefined
  ) => {
    const next = { ...configJson };

    const remove =
      value === undefined ||
      value === "" ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === "object" &&
        !Array.isArray(value) &&
        "gte" in value &&
        "lte" in value &&
        (value.gte === undefined || value.gte === "") &&
        (value.lte === undefined || value.lte === ""));

    if (remove) {
      delete next[key];
    } else {
      next[key] = value;
    }

    onChange({ ...preConfig, configJson: next });
  };

  const setRange = (
    key: string,
    side: "gte" | "lte",
    value: string,
    isDate?: boolean
  ) => {
    const current =
      (configJson[key] as { gte?: number | string; lte?: number | string }) ??
      (isDate ? { gte: "", lte: "" } : { gte: undefined, lte: undefined });

    const next = {
      ...current,
      [side]: isDate ? value : value.trim() === "" ? undefined : Number(value)
    };

    upsertConfigValue(key, next as StateFilterValue);
  };

  const getRangeValue = (key: string, side: "gte" | "lte") => {
    const range = configJson[key] as
      | { gte?: number | string; lte?: number | string }
      | undefined;
    const value = range?.[side];
    return value === undefined || value === null ? "" : String(value);
  };

  return (
    <Box
      mt="10px"
      p="12px"
      bg="#F7F8FA"
      borderRadius="6px"
      border="1px solid #E2E8F0"
    >
      <Text
        fontSize="11px"
        fontWeight="700"
        letterSpacing="0.08em"
        color="#69778C"
        mb="8px"
      >
        PRE-CONFIGURACAO - FILTRO
      </Text>
      <Grid templateColumns="1fr 1fr" gap="8px">
        <Box gridColumn="1 / -1">
          <Text fontSize="12px" color="#4A5568" mb="4px">
            Idade
          </Text>
          <MinMaxInput
            inputType="number"
            minValue={getRangeValue("idade", "gte")}
            maxValue={getRangeValue("idade", "lte")}
            onChangeMin={(v) => setRange("idade", "gte", v)}
            onChangeMax={(v) => setRange("idade", "lte", v)}
          />
        </Box>

        <Box gridColumn="1 / -1">
          <Text fontSize="12px" color="#4A5568" mb="4px">
            DDB
          </Text>
          <MinMaxInput
            inputType="date"
            minValue={getRangeValue("ddb", "gte")}
            maxValue={getRangeValue("ddb", "lte")}
            onChangeMin={(v) => setRange("ddb", "gte", v, true)}
            onChangeMax={(v) => setRange("ddb", "lte", v, true)}
          />
        </Box>

        <Box>
          <DropdownField
            title="Espécies"
            titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
            value={
              Array.isArray(configJson.especies) ? configJson.especies : []
            }
            options={especieOptions}
            onChange={(value) => {
              const current = Array.isArray(configJson.especies)
                ? (configJson.especies as number[])
                : [];
              const next = current.includes(value as number)
                ? current.filter((v) => v !== value)
                : [...current, value as number];
              upsertConfigValue("especies", next);
            }}
            dropdownProps={{
              w: "100%",
              variant: "filterInput",
              multiSelect: true
            }}
          />
        </Box>

        <Box>
          <DropdownField
            title="Possui representante legal"
            titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
            value={
              typeof configJson.possui_representante_legal === "boolean"
                ? configJson.possui_representante_legal
                : false
            }
            options={dropdownSimNao}
            onChange={(value) =>
              upsertConfigValue("possui_representante_legal", value as boolean)
            }
            dropdownProps={{ w: "100%", variant: "filterInput" }}
          />
        </Box>

        <Box>
          <DropdownField
            title="Meio de pagamento"
            titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
            value={
              typeof configJson.meio_pagamento === "string"
                ? configJson.meio_pagamento
                : ""
            }
            options={dropdownTipoPagamento}
            onChange={(value) =>
              upsertConfigValue(
                "meio_pagamento",
                (value as string) || undefined
              )
            }
            dropdownProps={{ w: "100%", variant: "filterInput" }}
          />
        </Box>

        <Box>
          <DropdownField
            title="Banco da operacao"
            titleProps={{ fontSize: "12px", color: "#4A5568", mb: "4px" }}
            value={
              Array.isArray(configJson.banco_operacao)
                ? configJson.banco_operacao
                : []
            }
            options={dropdownBancos}
            onChange={(value) => {
              const current = Array.isArray(configJson.banco_operacao)
                ? (configJson.banco_operacao as string[])
                : [];
              const next = current.includes(value as string)
                ? current.filter((v) => v !== value)
                : [...current, value as string];
              upsertConfigValue("banco_operacao", next);
            }}
            dropdownProps={{
              w: "100%",
              variant: "filterInput",
              multiSelect: true
            }}
            isLoading={false}
          />
        </Box>

        {[
          ["saldo_devedor", "Saldo devedor"],
          ["taxa_contrato", "Taxa contrato"],
          ["valor_parcela", "Valor parcela"],
          ["valor_liberado", "Valor liberado"],
          ["margem_aumento", "Margem aumento"],
          ["valor_beneficio", "Valor beneficio"],
          ["quantidade_contratos", "Qtd. contratos"],
          ["numero_parcelas_pagas", "Num. parcelas pagas"]
        ].map(([key, label]) => (
          <Box key={key} gridColumn="1 / -1">
            <Text fontSize="12px" color="#4A5568" mb="4px">
              {label}
            </Text>
            <MinMaxInput
              inputType="number"
              minValue={getRangeValue(key, "gte")}
              maxValue={getRangeValue(key, "lte")}
              onChangeMin={(v) => setRange(key, "gte", v)}
              onChangeMax={(v) => setRange(key, "lte", v)}
            />
          </Box>
        ))}
      </Grid>
      <Text fontSize="11px" mt="8px" color="#718096">
        Campos vazios nao sao enviados no configJson.
      </Text>
    </Box>
  );
}
